import { GA_MEASUREMENT_ID, GTAG_SCRIPT_URL } from "@mailhand/core/analytics";
import { type Route, parseLocation, subscribe } from "../app/router.js";

declare global {
  interface Window {
    dataLayer: unknown[];
  }
}

interface PageFields {
  page_location: string;
  page_title: string;
}

type GtagCommand = [command: "js", time: Date] | [command: "config", id: typeof GA_MEASUREMENT_ID, fields: PageFields & { send_page_view: false }] | [command: "event", name: "page_view", fields: PageFields];

/**
 * The path that Google Analytics sees for a route. It never holds an id, a sign-in
 * token, an invite token, or any query string.
 */
function analyticsPath(route: Route): string {
  switch (route.name) {
    case "login":
      return "/login";
    case "loginToken":
      return "/login/token";
    case "invite":
      return "/invite";
    case "start":
      return "/start";
    case "setup":
      return "/setup";
    case "activity":
      return "/activity";
    case "message":
      return "/messages/:id";
    case "actions":
      return "/actions";
    case "actionNew":
      return "/actions/new";
    case "actionEdit":
      return "/actions/:id";
    case "approvals":
      return "/approvals";
    case "settings":
      return "/settings";
  }
}

function pageFields(route: Route, origin: string): PageFields {
  return { page_location: `${origin}${analyticsPath(route)}`, page_title: route.name };
}

/**
 * Loads gtag.js and sends one page_view for each route the member opens.
 * The real URL never goes to Google, so the GA4 property must have
 * "Page changes based on browser history events" turned off.
 */
export function startAnalytics(win: Window): void {
  win.dataLayer = [];
  // gtag.js reads only `arguments` objects from the data layer, not arrays.
  function gtag(..._command: GtagCommand): void {
    // eslint-disable-next-line prefer-rest-params
    win.dataLayer.push(arguments);
  }

  const routeNow = (): Route => parseLocation(win.location.pathname, win.location.search);
  const firstRoute = routeNow();
  let sentPath = analyticsPath(firstRoute);
  gtag("js", new Date());
  gtag("config", GA_MEASUREMENT_ID, { ...pageFields(firstRoute, win.location.origin), send_page_view: false });
  gtag("event", "page_view", pageFields(firstRoute, win.location.origin));

  subscribe(() => {
    const route = routeNow();
    if (analyticsPath(route) === sentPath) return;
    sentPath = analyticsPath(route);
    gtag("event", "page_view", pageFields(route, win.location.origin));
  });

  const script = win.document.createElement("script");
  script.async = true;
  script.src = GTAG_SCRIPT_URL;
  win.document.head.append(script);
}
